import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ExternalLink, RefreshCw, AlertTriangle, Tv, Maximize2, Loader2, Video, Box } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { HlsVideoPlayer } from "./HlsVideoPlayer";

const DISCLAIMER_KEY = "ndakubet-stream-disclaimer-ack";
const VIEW_PREF_KEY = "ndakubet-stream-view-pref"; // "live" | "3d"

// Iter 124c — Hosts that enforce a server-side origin/referer check.
// We route these through `/api/streams/embed` which proxies the initial
// HTML AND all sub-resources through our domain with `Referer: aiscore.com`
// + `Origin: https://www.aiscore.com` injected.  Without this, the widget's
// own auth-check endpoint returns "This domain is unauthorized".
const REFERRER_PROTECTED_HOSTS = [
  "widgets.thesports01.com",
  "widgets.sports01.com",
  "tv.thesports01.com",
];

const buildIframeSrc = (url, sourceUrl) => {
  if (!url) return "";
  try {
    const host = new URL(url).hostname;
    if (REFERRER_PROTECTED_HOSTS.includes(host)) {
      const params = new URLSearchParams({ u: url });
      if (sourceUrl) params.set("page", sourceUrl);
      return `${API}/streams/embed?${params.toString()}`;
    }
  } catch {
    /* fall through to direct URL */
  }
  return url;
};

/**
 * LiveStreamModal — Iter 107
 *
 * Surfaces TWO independently-renderable feeds for a fixture:
 *   - **Live broadcast** (real video — HLS / iframe video player).
 *     Only available when aiscore exposed a manifest in the page source.
 *   - **3D Match Animation** (the always-on `widgets.thesports01.com`
 *     animated tracker iframe — momentum, ball position, attacks, etc.).
 *
 * The user picks which they want via a tab strip; their choice is
 * remembered in `localStorage[VIEW_PREF_KEY]` so subsequent matches
 * default to the same view.  When live video isn't available the
 * "Watch Live" tab is disabled with a soft tooltip.
 */
export const LiveStreamModal = ({ open, onOpenChange, fixtureId, homeTeam, awayTeam }) => {
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [acknowledged, setAcknowledged] = useState(() => {
    try { return localStorage.getItem(DISCLAIMER_KEY) === "1"; } catch { return false; }
  });
  // Active feed: "live" (real video) or "3d" (animation tracker).
  const [feed, setFeed] = useState(() => {
    try { return localStorage.getItem(VIEW_PREF_KEY) || "3d"; } catch { return "3d"; }
  });
  const [liveVideoFailed, setLiveVideoFailed] = useState(false);

  useEffect(() => {
    if (!open || !fixtureId) return;
    let cancelled = false;
    setLoading(true);
    setLiveVideoFailed(false);
    const params = new URLSearchParams();
    if (homeTeam) params.set("home", homeTeam);
    if (awayTeam) params.set("away", awayTeam);
    const qs = params.toString();
    axios.get(`${API}/streams/${fixtureId}${qs ? `?${qs}` : ""}`)
      .then((r) => {
        if (cancelled) return;
        setStream(r.data);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, fixtureId, homeTeam, awayTeam]);

  // If the user prefers "live" but the loaded stream has no live video,
  // soft-fall back to 3D so the modal isn't a no-op.  Runs whenever
  // the stream payload arrives.
  useEffect(() => {
    if (stream && !stream.live_video && feed === "live") setFeed("3d");
  }, [stream, feed]);

  // Persist user's tab preference whenever it changes.
  useEffect(() => {
    try { localStorage.setItem(VIEW_PREF_KEY, feed); } catch {}
  }, [feed]);

  const onLiveVideoError = () => {
    if (liveVideoFailed) return;
    setLiveVideoFailed(true);
    setFeed("3d");
    toast.info("Live broadcast dropped — switched to 3D match animation");
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (homeTeam) params.set("home", homeTeam);
      if (awayTeam) params.set("away", awayTeam);
      const qs = params.toString();
      await axios.post(`${API}/streams/${fixtureId}/refresh${qs ? `?${qs}` : ""}`);
      const r = await axios.get(`${API}/streams/${fixtureId}${qs ? `?${qs}` : ""}`);
      setStream(r.data);
      if (r.data?.available) toast.success("Fresh stream link loaded");
      else toast.info("Still no public stream — try again in a minute");
    } catch { toast.error("Couldn't reach the stream provider"); }
    finally { setRefreshing(false); }
  };

  const acceptDisclaimer = () => {
    try { localStorage.setItem(DISCLAIMER_KEY, "1"); } catch {}
    setAcknowledged(true);
  };

  const hasLive = !!stream?.live_video?.url && !liveVideoFailed;
  const hasAnim = !!stream?.animation_3d?.url;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="live-stream-modal"
        className="max-w-4xl bg-background border-border text-foreground rounded-2xl p-0 overflow-hidden"
      >
        <DialogHeader className="p-5 border-b border-border">
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-bold tracking-[-0.02em]">
            <Tv className="w-4 h-4 text-primary" />
            {homeTeam} vs {awayTeam}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Live coverage · provided by aiscore.com
          </DialogDescription>
        </DialogHeader>

        {!acknowledged ? (
          <div className="p-6 space-y-4" data-testid="stream-disclaimer">
            <div className="flex items-start gap-3 rounded-xl border border-warning/40 bg-warning/10 p-4">
              <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
              <div className="text-sm">
                <div className="font-semibold mb-1">Third-party stream notice</div>
                <p className="text-muted-foreground">
                  Live coverage is embedded from aiscore.com and its widget
                  partners. NDAKUBET does not host or own any broadcast
                  rights. You are responsible for ensuring streaming is
                  legal in your country.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="h-9 rounded-full px-4 border-border bg-card hover:bg-muted text-foreground"
                data-testid="stream-disclaimer-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={acceptDisclaimer}
                data-testid="stream-disclaimer-accept"
                className="h-9 rounded-full px-4 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              >
                I understand — continue
              </Button>
            </div>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Searching for stream…
          </div>
        ) : !stream?.available ? (
          <div className="p-8 text-center space-y-4" data-testid="stream-unavailable">
            <Tv className="w-10 h-10 text-muted-foreground mx-auto" />
            <h3 className="font-display text-xl font-semibold">No public coverage available</h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              We couldn't find a stream or 3D animation for this match. You can still follow the in-play stats and LMI triggers.
            </p>
            <div className="flex items-center justify-center gap-2 pt-2">
              <Button
                onClick={refresh}
                disabled={refreshing}
                variant="outline"
                className="h-9 rounded-full px-4 border-border bg-card hover:bg-muted text-foreground"
                data-testid="stream-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
                {refreshing ? "Searching" : "Try again"}
              </Button>
              <Link to={`/live/${fixtureId}`}>
                <Button
                  onClick={() => onOpenChange(false)}
                  className="h-9 rounded-full px-4 bg-foreground text-background hover:bg-foreground/90"
                  data-testid="stream-open-live-page"
                >
                  Open live tracker
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          <div data-testid="stream-player-wrap">
            {/* Two-feed switcher (Iter 107) — Live broadcast vs 3D animation */}
            <div className="flex items-center gap-1 p-2.5 border-b border-border bg-card/40">
              <button
                type="button"
                onClick={() => hasLive && setFeed("live")}
                disabled={!hasLive}
                data-testid="stream-tab-live"
                title={hasLive ? "Live broadcast" : "No live broadcast available"}
                className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-semibold transition-colors ${
                  feed === "live" && hasLive
                    ? "bg-foreground text-background"
                    : hasLive
                    ? "text-muted-foreground hover:text-foreground bg-muted"
                    : "text-muted-foreground/40 cursor-not-allowed bg-muted/40"
                }`}
              >
                <Video className="w-3.5 h-3.5" />
                Watch Live
                {!hasLive && <span className="text-[9px] opacity-60 ml-1">·N/A</span>}
              </button>
              <button
                type="button"
                onClick={() => hasAnim && setFeed("3d")}
                disabled={!hasAnim}
                data-testid="stream-tab-3d"
                title="3D match animation tracker"
                className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-semibold transition-colors ${
                  feed === "3d"
                    ? "bg-foreground text-background"
                    : hasAnim
                    ? "text-muted-foreground hover:text-foreground bg-muted"
                    : "text-muted-foreground/40 cursor-not-allowed bg-muted/40"
                }`}
              >
                <Box className="w-3.5 h-3.5" />
                3D Match Animation
              </button>
            </div>

            <div className="relative aspect-video bg-black">
              {feed === "live" && hasLive ? (
                stream.live_video.kind === "hls" ? (
                  <HlsVideoPlayer
                    src={stream.live_video.url}
                    onError={onLiveVideoError}
                    className="absolute inset-0 w-full h-full"
                  />
                ) : (
                  <iframe
                    src={stream.live_video.url}
                    title={`${homeTeam} vs ${awayTeam} — live`}
                    allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                    allowFullScreen
                    sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
                    className="absolute inset-0 w-full h-full border-0"
                    data-testid="stream-iframe-live"
                  />
                )
              ) : hasAnim ? (
                /* Iter 124d — The thesports01 3D widget's live-data API
                   sits behind Cloudflare which blocks our server IP.
                   We attempted server-side proxying of the HTML + JS
                   bundles (works) but the runtime XHR calls still 403.
                   Result: the iframe loads but stays on the "this domain
                   is unauthorized" splash.

                   Honest UX: show a clear CTA that opens the 3D tracker
                   directly on aiscore (where it renders perfectly).  We
                   keep the iframe rendering underneath so any future
                   client-side breakthrough still surfaces it. */
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 p-6 text-center bg-gradient-to-br from-emerald-950/40 via-black to-black">
                  <Box className="w-12 h-12 text-emerald-400/80" />
                  <div>
                    <div className="font-bold text-base">3D Match Tracker</div>
                    <div className="text-sm text-muted-foreground mt-1 max-w-md">
                      The live 3D animation is hosted by aiscore and blocks inline embeds.
                      Open it in a new tab for the full real-time view.
                    </div>
                  </div>
                  <a
                    href={stream.source_url || stream.animation_3d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="stream-open-3d-aiscore"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold text-sm shadow-lg shadow-emerald-500/30 transition"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Open 3D Tracker on aiscore
                  </a>
                  <div className="text-[10px] text-muted-foreground/70 uppercase tracking-wider font-bold">
                    {homeTeam} vs {awayTeam}
                  </div>
                </div>
              ) : null}
              {(stream.source_url || stream.live_tracker_url) && (
                <a
                  href={stream.source_url || stream.live_tracker_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="stream-modal-open-source"
                  className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold hover:bg-black/90 transition-colors opacity-80 hover:opacity-100"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open on aiscore
                </a>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 border-t border-border">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Badge className="bg-muted text-foreground border border-border text-[10px]">
                  source: {stream.source}
                </Badge>
                <span className="text-[10px]">
                  {feed === "live" ? "Live broadcast" : "3D animation tracker"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={refresh}
                  disabled={refreshing}
                  variant="outline"
                  size="sm"
                  className="h-8 rounded-full px-3 border-border bg-card hover:bg-muted text-foreground"
                  data-testid="stream-refresh-inline-btn"
                >
                  <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
                <Link to={`/live/${fixtureId}`}>
                  <Button
                    onClick={() => onOpenChange(false)}
                    size="sm"
                    className="h-8 rounded-full px-3 bg-foreground text-background hover:bg-foreground/90"
                    data-testid="stream-fullpage-btn"
                  >
                    <Maximize2 className="w-3 h-3 mr-1" /> Full page
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LiveStreamModal;
